/* istanbul ignore file */
import { makeFragmentData } from '@packages/gql/generated/shopping';
import {
  DownloadItemFragmentFragmentDoc,
  ProductPriceFragmentFragmentDoc,
} from '@packages/gql/generated/shopping/graphql';
import type { EfficiencyFlagProps } from '@packages/shared';
import { Box, EfficiencyFlag, Image, Typography, Stack } from '@packages/shared';
import { Fragment } from 'react';
import { useIntl } from 'react-intl';
import { DownloadItem } from '../../DownloadItem';
import { Price } from '../../Price';
import { ServiceItems } from '../ServiceItems';
import { LegacyEnergyFlag } from './LegacyEnergyFlag';
import { useBasketData } from './useBasketData';
import { usePriceFormater } from '../../../hooks/usePriceFormater';
import type { TaxHintProps } from '../../TaxHint';
import { TaxHint } from '../../TaxHint';

// TODO: check for mapper
const mapTireEfficiency = (text?: string): EfficiencyFlagProps['type'] => {
  switch (text) {
    case 'Nasshaftung':
      return 'wetGrip';
    case 'Rollgeraeusch':
      return 'externalRollingNoise';
    default:
      return 'fuelEfficiency';
  }
};

type AddedProductProps = {
  lineItems?: Array<string>;
  taxHintData: TaxHintProps['data'];
};

// TODO(api-layer): needs to be refactored with gql fragments when basket resolvers are implemented
export const AddedProduct = ({ lineItems, taxHintData }: AddedProductProps) => {
  const { formatMessage } = useIntl();
  const formatPrice = usePriceFormater('ATB');

  const newLineItem = (lineItems && lineItems.length > 0 && lineItems[0]) || null;
  const { data } = useBasketData(newLineItem);
  if (!data) {
    return null;
  }

  const product = data.products.find((p) => p.lineItemId === newLineItem);

  if (!product) {
    return null;
  }
  const {
    imageUrl,
    name,
    variation,
    deliveryMessage,
    deliveryTextColor,
    price,
    quantity,
    singlePrice,
    powerEfficiency,
    aplus,
  } = product;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Box
          sx={{ flexShrink: 0, position: 'relative', width: [80, 80, 120], height: [80, 80, 120] }}
        >
          <Image src={imageUrl} style={{ objectFit: 'contain' }} fill alt="" sizes="25vw" />
        </Box>
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography
            sx={{
              typography: ['h6', 'h6', 'h5'],
              fontWeight: ['fontWeightSemiBold', 'fontWeightSemiBold', 'normal'],
            }}
          >
            {name}
          </Typography>
          <Typography variant="body2">{variation}</Typography>
          <Box>
            <Typography variant="body2">
              {formatMessage(
                {
                  id: 'atblayer.singleprice.label',
                  defaultMessage: 'Anzahl {quantity} (á {singlePrice})',
                },
                {
                  quantity,
                  singlePrice: formatPrice(singlePrice.value, singlePrice.currencyCode),
                },
              )}
            </Typography>
            <Typography variant="body2" sx={{ color: `${[deliveryTextColor]}.main` }}>
              {deliveryMessage}
            </Typography>
          </Box>
          <Box
            sx={{ display: 'flex', flexDirection: ['column', 'column', 'row'], gap: 1, mt: 1.5 }}
          >
            <Box sx={{ flexGrow: 1 }}>
              {powerEfficiency && powerEfficiency.length > 0 && (
                <>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {powerEfficiency.map((p, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Fragment key={i}>
                        {p.iconText ? (
                          <EfficiencyFlag
                            text={p.iconText!}
                            overlay={{ title: p.text || '', imgUrl: p.link }}
                            type={mapTireEfficiency(p.text) as EfficiencyFlagProps['type']}
                          />
                        ) : (
                          <LegacyEnergyFlag
                            icon={`${p.icon}.svg`}
                            overlayImg={p.link}
                            overlayTitle={p.text}
                          />
                        )}
                      </Fragment>
                    ))}
                  </Box>
                  <Stack spacing={1} mt={1}>
                    {powerEfficiency.map(
                      (item, idx) =>
                        item.dataSheet && (
                          <DownloadItem
                            data={makeFragmentData(
                              { link: item.dataSheet, type: 'PRODUCT_DATASHEET' },
                              DownloadItemFragmentFragmentDoc,
                            )}
                            // index should only be shown, if there is more then one download
                            idx={powerEfficiency.length > 1 ? idx + 1 : undefined}
                            renderIcon={false}
                            // eslint-disable-next-line react/no-array-index-key
                            key={idx}
                          />
                        ),
                    )}
                  </Stack>
                </>
              )}
            </Box>
            <Stack>
              <Price
                priceData={makeFragmentData(
                  {
                    ...price,
                  },
                  ProductPriceFragmentFragmentDoc,
                )}
                alignment="right"
                small
              />
              <TaxHint data={taxHintData} />
            </Stack>
          </Box>
        </Box>
      </Box>
      {aplus && newLineItem && <ServiceItems serviceItems={aplus} pli={newLineItem} />}
    </Box>
  );
};
